import React, { useEffect, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Box, Container, Typography, Button, Stack, styled } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Footer from '../componentsPeopletech/Footer/Footer';

const SEOExample = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const baseUrl = 'https://www.peoplefortech.com';
  const currentLang = i18n.language || 'en';

  // Scroll al inicio cuando se carga la página (useLayoutEffect se ejecuta antes del render)
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // También en useEffect por si acaso
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const exampleCode = `<!DOCTYPE html>
<html lang="es">
<head>
  <title>Tu Empresa</title>

  <!-- Schema.org JSON-LD -->
  <script type="application/ld+json">
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Tu Empresa",
    "url": "https://www.tuempresa.com",
    "email": "contacto@tuempresa.com"
  }
  </script>
</head>
<body>
  <h1>Bienvenido</h1>
</body>
</html>`;

  // Snapshot de métricas internas SEO/GEO (actualizadas en el último análisis)
  const seoProfile = {
    totalInternalLinks: 72,
    totalUrls: 35,
    orphanUrls: 8,
    geoUrlShare: 11.11,
    linkedUrlsShare: 77.14, // (35 - 8) / 35
    topUrls: [
      { url: '/contact', share: 15.28, links: 11 },
      { url: '/', share: 13.89, links: 10 },
      { url: '/about', share: 11.11, links: 8 },
      { url: '/geo-para-chatgpt-gemini', share: 11.11, links: 8 },
      { url: '/blog', share: 9.72, links: 7 },
    ],
  };

  // Background video container (igual que en HomePeople)
  const BackgroundVideoContainer = styled(Box)({
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: -1,
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(255, 255, 255, 0.85)',
      zIndex: 1,
    },
  });

  const Video = styled('video')({
    position: 'absolute',
    top: '50%',
    left: '50%',
    minWidth: '100%',
    minHeight: '100%',
    width: 'auto',
    height: 'auto',
    transform: 'translate(-50%, -50%)',
    filter: 'blur(5px)',
    objectFit: 'cover',
    zIndex: 0,
    opacity: 1,
  });

  const ContentWrapper = styled(Box)({
    position: 'relative',
    zIndex: 1,
    backgroundColor: 'transparent',
  });

  // IDE Container
  const IDEContainer = styled(Box)({
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    backgroundColor: '#ffffff',
    marginTop: '32px',
  });

  // IDE Header (pestañas)
  const IDEHeader = styled(Box)(({ theme }) => ({
    backgroundColor: '#2d2d2d',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #1e1e1e',
  }));

  const Tab = styled(Box)(({ active }) => ({
    padding: '12px 20px',
    backgroundColor: active ? '#1e1e1e' : 'transparent',
    color: active ? '#ffffff' : '#a0a0a0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontFamily: '"Montserrat", sans-serif',
    transition: 'all 0.2s',
    borderRight: '1px solid #1e1e1e',
    '&:hover': {
      backgroundColor: active ? '#1e1e1e' : '#252525',
    },
  }));

  const TabDot = styled(Box)(({ active }) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: active ? '#4EC9B0' : '#5a5a5a',
  }));

  // Code content area
  const CodeContent = styled(Box)({
    backgroundColor: '#1e1e1e',
    padding: '24px',
    overflowX: 'auto',
    fontFamily: '"Fira Code", "Consolas", "Monaco", "Courier New", monospace',
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#ffffff',
  });

  return (
    <>
      <Helmet>
        <title>{t('seoExample.title')} - PeopleForTech</title>
        <meta name="description" content={t('seoExample.subtitle')} />
        <link rel="canonical" href={`${baseUrl}/geo-para-chatgpt-gemini`} />
        <html lang={currentLang} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${baseUrl}/geo-para-chatgpt-gemini`} />
        <meta property="og:title" content={`${t('seoExample.title')} - PeopleForTech`} />
        <meta property="og:description" content={t('seoExample.subtitle')} />
        
        {/* Schema.org JSON-LD para el servicio */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "GEO - Implementación de Schema.org",
            "description": "Te ayudamos a implementar Schema.org/JSON-LD en tu sitio web para mejorar tu GEO",
            "provider": {
              "@type": "Organization",
              "name": "PeopleForTech",
              "url": baseUrl,
              "email": "contact@peoplefortech.com"
            },
            "serviceType": "Generative Engine Optimization (GEO)",
            "url": `${baseUrl}/servicios/geo`,
            "areaServed": "Worldwide"
          })}
        </script>
      </Helmet>

      <BackgroundVideoContainer>
        <Video
          autoPlay
          loop
          muted
          playsInline
          src="/18192389-hd_1920_1080_60fps.mp4"
        >
          Tu navegador no soporta la reproducción de videos.
        </Video>
      </BackgroundVideoContainer>

      <ContentWrapper>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Stack spacing={6}>
            {/* Título general y descripción */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: 'black',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                {t('seoExample.title')}
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  color: 'black',
                  fontSize: '1.1rem',
                }}
              >
                {t('seoExample.subtitle')}
              </Typography>
            </Box>

            {/* Perfil SEO/GEO interno estilo "backlink profile" */}
            <Box
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: 3,
                p: { xs: 2, md: 3 },
                boxShadow: '0 12px 36px rgba(0,0,0,0.12)',
              }}
            >
              <Typography
                variant="h4"
                component="h2"
                sx={{ fontWeight: 700, color: '#111', mb: 1.5, fontSize: { xs: '1.4rem', md: '1.9rem' } }}
              >
                Backlink profile interno (SEO/GEO)
              </Typography>
              <Typography variant="body2" sx={{ color: '#555', mb: 3 }}>
                Distribución de enlaces internos por URL. Más porcentaje = mayor prioridad interna.
              </Typography>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
                <Box sx={{ flex: 1, p: 2, borderRadius: 2, border: '1px solid #ececec', backgroundColor: '#fafafa' }}>
                  <Typography variant="overline" sx={{ color: '#6b6b6b' }}>
                    URL share (GEO principal)
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#111' }}>
                    {seoProfile.geoUrlShare}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    /geo-para-chatgpt-gemini
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, p: 2, borderRadius: 2, border: '1px solid #ececec', backgroundColor: '#fafafa' }}>
                  <Typography variant="overline" sx={{ color: '#6b6b6b' }}>
                    Enlaces internos totales
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#111' }}>
                    {seoProfile.totalInternalLinks}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Inbound links detectados
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, p: 2, borderRadius: 2, border: '1px solid #ececec', backgroundColor: '#fafafa' }}>
                  <Typography variant="overline" sx={{ color: '#6b6b6b' }}>
                    Cobertura de URLs enlazadas
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#111' }}>
                    {seoProfile.linkedUrlsShare}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {seoProfile.totalUrls - seoProfile.orphanUrls}/{seoProfile.totalUrls} URLs con enlaces
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ borderTop: '1px solid #ececec', pt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111', mb: 1.5 }}>
                  Top URLs por porcentaje interno
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e5e5e5', color: '#444' }}>URL</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e5e5e5', color: '#444' }}>Porcentaje</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e5e5e5', color: '#444' }}>Inbound links</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seoProfile.topUrls.map((row) => (
                        <tr key={row.url}>
                          <td style={{ padding: '10px 8px', borderBottom: '1px solid #f0f0f0', color: '#111' }}>{row.url}</td>
                          <td style={{ padding: '10px 8px', borderBottom: '1px solid #f0f0f0', color: '#111', fontWeight: 700 }}>{row.share}%</td>
                          <td style={{ padding: '10px 8px', borderBottom: '1px solid #f0f0f0', color: '#111' }}>{row.links}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            </Box>

            {/* 1. Cómo lo hacemos: texto + imagen */}
            <Box>
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: 'black',
                  mb: 2,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                }}
              >
                {t('seoExample.section1Title')}
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  color: 'black',
                  fontSize: '1.05rem',
                  mb: 3,
                }}
              >
                {t('seoExample.section1Subtitle')}
              </Typography>

              <Box
                sx={(theme) => ({
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mt: 2,
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                  backgroundColor: '#ffffff',
                  p: { xs: 1.5, md: 2 },
                  maxWidth: '960px',
                  marginInline: 'auto',
                  '& img': {
                    width: '100%',
                    height: 'auto',
                    borderRadius: 2,
                  },
                })}
              >
                <img
                  src="/SEO_IA_1.png"
                  alt="Proceso de GEO"
                  loading="lazy"
                />
              </Box>
            </Box>

            {/* 2. Cómo lo hacemos técnicamente: IDE con código */}
            <Box>
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: 'black',
                  mb: 2,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                }}
              >
                {t('seoExample.section2Title')}
              </Typography>

              <IDEContainer>
                {/* IDE Header con pestañas */}
                <IDEHeader>
                  <Tab active={true}>
                    <TabDot active={true} />
                    <span>index.html</span>
                  </Tab>
                  <Box sx={{ flex: 1, backgroundColor: '#2d2d2d' }} />
                  <Box
                    sx={{
                      padding: '8px 16px',
                      color: '#a0a0a0',
                      fontSize: '12px',
                      fontFamily: '"Montserrat", sans-serif',
                    }}
                  >
                    HTML
                  </Box>
                </IDEHeader>

                {/* Code Content */}
                <CodeContent>
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: 'pre',
                      overflowX: 'auto',
                    }}
                  >
                    {exampleCode}
                  </pre>
                </CodeContent>
              </IDEContainer>
            </Box>

            {/* 3. Pruebas en ChatGPT / Gemini y CTA */}
            <Box>
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: 'black',
                  mb: 2,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                }}
              >
                {t('seoExample.section3Title')}
              </Typography>

              {/* Imagen del paso 3 */}
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mt: 2,
                  mb: 4,
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                  backgroundColor: '#ffffff',
                  p: { xs: 1.5, md: 2 },
                  maxWidth: '960px',
                  marginInline: 'auto',
                  '& img': {
                    width: '100%',
                    height: 'auto',
                    borderRadius: 2,
                  },
                })}
              >
                <img
                  src="/image_seo_gpt.png"
                  alt="Pruebas de GEO en ChatGPT y Gemini"
                  loading="lazy"
                />
              </Box>

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={4}
                alignItems="center"
                justifyContent="space-between"
              >
                {/* Iconos de IAs */}
                <Stack
                  direction="row"
                  spacing={3}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                      backgroundColor: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 1,
                    }}
                  >
                    <img
                      src="/gpt_icon.png"
                      alt="ChatGPT icon"
                      style={{ width: '65%', height: '65%', objectFit: 'contain' }}
                    />
                  </Box>

                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                      backgroundColor: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 1,
                    }}
                  >
                    <img
                      src="/gemini_icon.png"
                      alt="Gemini icon"
                      style={{ width: '65%', height: '65%', objectFit: 'contain' }}
                    />
                  </Box>
                </Stack>

                {/* Texto + botones */}
                <Box sx={{ maxWidth: 520 }}>
                  <Typography
                    variant="body1"
                    paragraph
                    sx={{
                      color: '#000',
                      fontSize: '1.05rem',
                      lineHeight: 1.8,
                    }}
                  >
                    {t('seoExample.helpDescription')}
                  </Typography>

                  <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="large"
                      className="glow-on-hover"
                      onClick={() => navigate('/contact')}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        backgroundColor: '#111',
                        color: '#ffffff',
                        borderRadius: 10,
                        boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
                        position: 'relative',
                        zIndex: 0,
                        '&:hover': {
                          backgroundColor: '#111',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                        },
                        '&:active': {
                          color: '#000',
                        },
                      }}
                    >
                      {t('seoExample.contactUs')}
                    </Button>
                    <Button
                      size="large"
                      className="glow-on-hover"
                      onClick={() => navigate('/')}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        backgroundColor: '#111',
                        color: '#ffffff',
                        borderRadius: 10,
                        boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
                        position: 'relative',
                        zIndex: 0,
                        '&:hover': {
                          backgroundColor: '#111',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                        },
                        '&:active': {
                          color: '#000',
                        },
                      }}
                    >
                      {t('seoExample.backHome')}
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Container>

        <Footer />
      </ContentWrapper>
    </>
  );
};

export default SEOExample;
